import axios from 'axios';

// Address search against the geolocation service's Google-backed
// autocomplete. Google bills per *session*: every /autocomplete call while
// the member types, plus the one /place call when they pick a suggestion,
// must carry the same sessionToken, and a token must never be used again
// after that. This module owns that token, the debounce and the minimum
// term length, so the store and the components do not have to.
//
//   const session = createAddressAutocomplete({ getBaseUrl });
//   session.search(term)        // debounced; null when a newer search took over
//   session.autocomplete(term)  // at once, same token (the street-number lookup)
//   session.place(placeId)      // hands the token to /place and throws it away
//   session.reset()             // throws the token away (field cleared, view left)

export const DEBOUNCE_MS = 250;
export const MIN_TERM_LENGTH = 3;

const EMPTY = { status: 'ZERO_RESULTS', predictions: [] };
const ERROR = { status: 'ERROR', predictions: [] };

export function newSessionToken() {
  const crypto = typeof window !== 'undefined' ? window.crypto : undefined;
  if (crypto && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // RFC 4122 v4 for browsers without randomUUID.
  const bytes = new Uint8Array(16);
  if (crypto && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
    12,
    16
  )}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function normalise(data) {
  const status = data && data.status ? data.status : 'ERROR';
  const predictions =
    data && Array.isArray(data.predictions) ? data.predictions : [];
  return status === 'ERROR' ? { ...ERROR } : { status, predictions };
}

export function createAddressAutocomplete({
  getBaseUrl,
  http = axios,
  debounceMs = DEBOUNCE_MS,
  minLength = MIN_TERM_LENGTH
}) {
  let token = null;
  let timer = null;
  let resolvePending = null;
  let requestSeq = 0;

  const ensureToken = () => {
    if (!token) token = newSessionToken();
    return token;
  };

  // Drops a search that is still waiting for its debounce.
  const cancelPending = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    if (resolvePending) resolvePending(null);
    resolvePending = null;
  };

  async function request(term) {
    try {
      const res = await http.get(`${getBaseUrl()}/autocomplete`, {
        params: { searchTerm: term, sessionToken: ensureToken() }
      });
      return normalise(res && res.data);
    } catch (error) {
      // 502 with { status: "ERROR" }, or no answer at all: an empty list.
      return { ...ERROR };
    }
  }

  // Trimmed term, or the empty-list answer it gets without a request.
  function prepare(term) {
    const trimmed = (term || '').trim();
    if (!trimmed) {
      reset();
      return { answer: { ...EMPTY, tooShort: true } };
    }
    if (trimmed.length < minLength) {
      ensureToken();
      return { answer: { ...EMPTY, tooShort: true } };
    }
    return { trimmed };
  }

  function search(term) {
    cancelPending();
    const { answer, trimmed } = prepare(term);
    if (answer) return Promise.resolve(answer);

    return new Promise(resolve => {
      resolvePending = resolve;
      timer = setTimeout(async () => {
        timer = null;
        resolvePending = null;
        const seq = ++requestSeq;
        const result = await request(trimmed);
        // A newer request went out meanwhile: this answer is stale.
        resolve(seq === requestSeq ? result : null);
      }, debounceMs);
    });
  }

  function autocomplete(term) {
    const { answer, trimmed } = prepare(term);
    if (answer) return Promise.resolve(answer);
    requestSeq++;
    return request(trimmed);
  }

  function place(placeId) {
    cancelPending();
    const sessionToken = token;
    token = null;
    return http.get(`${getBaseUrl()}/place`, {
      params: sessionToken ? { id: placeId, sessionToken } : { id: placeId }
    });
  }

  function reset() {
    cancelPending();
    token = null;
  }

  return {
    search,
    autocomplete,
    place,
    reset,
    currentToken: () => token
  };
}
